/**
 * Extract a Cartographer Skeleton from a three.js GLTF scene.
 *
 * This takes Approach B from the plan: walk the three.js Bone hierarchy
 * produced by GLTFLoader (already T-posed with correct rest transforms)
 * and map it into our Cartographer Bone/Skeleton model.
 *
 * Mirrors the logic of cw-cartographer/cartographer/formats/gltf/importer.py
 * but uses the already-parsed three.js result rather than pygltflib.
 */

import * as THREE from 'three';

import {
  Bone as CartographerBone,
  EulerOrder,
  type EulerOrderSource,
  Skeleton as CartographerSkeleton,
} from './core';
import {
  createTransformOutlinerNode,
  getBoneOutlinerNodeId,
  getTransformOutlinerNodeId,
  shouldIncludeTransformObject,
  type OutlinerHierarchy,
  type OutlinerTransformNode,
} from '../outliner';
import {
  createFbxUpAxisCorrection,
  type FbxUpAxisMetadata,
} from '../fbx';
import { normalizeQuaternion, parseEulerOrder } from './rotationService';
import { sourceNameFromThreeBone } from './threeBoneSourceName';

type ResolvedEulerOrder = {
  order: EulerOrder;
  source: EulerOrderSource;
};

export type ExtractSkeletonOptions = {
  fbxUpAxisMetadata?: FbxUpAxisMetadata | null;
};

const SIGNATURE_DECIMALS = 4;
const WRAPPER_TRANSFORM_EPSILON = 1e-6;

function roundSignatureValue(value: number): string {
  return value.toFixed(SIGNATURE_DECIMALS);
}

function isIdentityLocalTransform(obj: THREE.Object3D): boolean {
  const p = obj.position;
  const q = obj.quaternion;
  const s = obj.scale;
  return (
    Math.abs(p.x) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(p.y) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(p.z) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(q.x) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(q.y) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(q.z) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(q.w - 1) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(s.x - 1) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(s.y - 1) <= WRAPPER_TRANSFORM_EPSILON &&
    Math.abs(s.z - 1) <= WRAPPER_TRANSFORM_EPSILON
  );
}

function buildBoneNamePath(bone: THREE.Bone): string[] {
  const parts: string[] = [];
  let current: THREE.Object3D | null = bone;
  let previousBone: THREE.Bone | null = null;
  while (current) {
    if (current instanceof THREE.Bone) {
      const sameNameAsChild =
        previousBone !== null && previousBone.name === current.name;
      const previousIsIdentity =
        previousBone !== null && isIdentityLocalTransform(previousBone);
      const isWrapperPair =
        sameNameAsChild &&
        (previousIsIdentity || isIdentityLocalTransform(current));
      if (!isWrapperPair) {
        parts.push(current.name);
      }
      previousBone = current;
    }
    current = current.parent;
  }
  parts.reverse();
  return parts;
}

function buildBoneIdentitySignature(bone: THREE.Bone): string {
  const elements = bone.matrixWorld.elements;
  const path = buildBoneNamePath(bone).join('/');
  return `${path}|${roundSignatureValue(elements[12])},${roundSignatureValue(elements[13])},${roundSignatureValue(elements[14])}`;
}

function formatSignedAxisLabel(
  axis: FbxUpAxisMetadata['upAxis'],
  sign: FbxUpAxisMetadata['upSign'],
): string {
  return `${sign > 0 ? '+' : '-'}${axis.toUpperCase()}`;
}

function resolveBoneEulerOrder(bone: THREE.Bone): ResolvedEulerOrder {
  const transformDataOrder = parseEulerOrder(
    (bone.userData?.transformData as { eulerOrder?: unknown } | undefined)
      ?.eulerOrder,
  );
  if (transformDataOrder) {
    return { order: transformDataOrder, source: 'transformData' };
  }

  const objectOrder = parseEulerOrder(bone.rotation?.order);
  if (objectOrder) return { order: objectOrder, source: 'objectRotation' };

  return { order: EulerOrder.XYZ, source: 'default' };
}

/**
 * Find the first SkinnedMesh in a scene graph, preferring the one with
 * the most joints (largest skeleton).
 */
/**
 * Collect ALL unique Bone objects from every SkinnedMesh in the scene.
 * FBX files often split a character across multiple SkinnedMeshes (body,
 * face, accessories), each with a different bone subset.  We need the
 * union of all bones to build the complete skeleton.
 * Also returns the first SkinnedMesh found (for deforming-joint detection).
 */
function collectAllBones(
  root: THREE.Object3D,
): { bones: THREE.Bone[]; primaryMesh: THREE.SkinnedMesh | null } {
  root.updateMatrixWorld(true);
  const boneSet = new Set<THREE.Bone>();
  const meshes: THREE.SkinnedMesh[] = [];

  root.traverse((obj) => {
    if (obj instanceof THREE.SkinnedMesh && obj.skeleton) {
      meshes.push(obj);
      for (const b of obj.skeleton.bones) {
        boneSet.add(b);
      }
    }
  });

  // Walk descendants of collected bones to find child Bone objects
  // that aren't in any SkinnedMesh's skeleton.bones (e.g. toe end
  // bones, leaf bones).  This ensures the full hierarchy is captured.
  function walkDescendants(node: THREE.Object3D) {
    for (const child of node.children) {
      if (child instanceof THREE.Bone && !boneSet.has(child)) {
        boneSet.add(child);
        walkDescendants(child);
      }
    }
  }
  for (const bone of [...boneSet]) {
    walkDescendants(bone);
  }

  // Fallback: if no SkinnedMesh was found, scan the entire scene for Bone
  // objects. This handles models that were re-exported with bones in the
  // hierarchy but without proper skinning (e.g. manually edited in Blender).
  if (boneSet.size === 0) {
    root.traverse((obj) => {
      if (obj instanceof THREE.Bone) {
        boneSet.add(obj);
      }
    });
    if (boneSet.size > 0) {
      console.log(
        `[Cartographer] No SkinnedMesh found, discovered ${boneSet.size} Bone(s) from scene hierarchy`,
      );
    }
  }

  // FBX files with multiple SkinnedMeshes often create separate Bone
  // objects per mesh for the same logical bone. Some copies carry real
  // TRS rest-pose data while others have zero position (the FBXLoader
  // uses boneInverses instead). Deduplicate by hierarchy signature to
  // preserve same-name bones that live in different branches.
  const bySignature = new Map<string, THREE.Bone>();
  for (const bone of boneSet) {
    const signature = buildBoneIdentitySignature(bone);
    const existing = bySignature.get(signature);
    if (!existing || bone.position.lengthSq() > existing.position.lengthSq()) {
      bySignature.set(signature, bone);
    }
  }
  const deduped = [...bySignature.values()];
  if (deduped.length < boneSet.size) {
    console.log(
      `[Cartographer] Deduplicated bones: ${boneSet.size} → ${deduped.length} (by hierarchy signature, preferring real TRS)`,
    );
  }

  const primaryMesh = meshes.reduce<THREE.SkinnedMesh | null>(
    (best, m) =>
      !best || m.skeleton.bones.length > best.skeleton.bones.length
        ? m
        : best,
    null,
  );

  return { bones: deduped, primaryMesh };
}

/**
 * Build a rest-local matrix for a three.js Bone.
 *
 * GLTFLoader stores the local rest transform in `bone.matrix` when
 * `bone.matrixAutoUpdate` is true and the bone hasn't been animated.
 * We decompose from TRS just like the Python importer.
 */
function boneRestLocal(obj: THREE.Object3D): THREE.Matrix4 {
  const m = new THREE.Matrix4();
  m.compose(obj.position, obj.quaternion, obj.scale);
  return m;
}


/**
 * Determine whether a synthetic (identity-transform) root wrapper is
 * needed — mirrors GLTFSkeletonImporter._needs_synthetic_root.
 */
function needsSyntheticRoot(restLocal: THREE.Matrix4): boolean {
  const e = restLocal.elements;
  // col-major: translation is e[12], e[13], e[14]
  const tx = e[12],
    ty = e[13],
    tz = e[14];
  const hasTranslation =
    Math.abs(tx) > 1e-6 || Math.abs(ty) > 1e-6 || Math.abs(tz) > 1e-6;
  if (hasTranslation) return true;

  // Check if upper-left 3x3 ≈ identity
  const ident = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const rot = [e[0], e[1], e[2], e[4], e[5], e[6], e[8], e[9], e[10]];
  for (let i = 0; i < 9; i++) {
    if (Math.abs(rot[i] - ident[i]) > 1e-6) return true;
  }
  return false;
}

function collectHierarchyTransforms(
  bone: THREE.Bone,
  resolvedParentBone: THREE.Bone | null,
): THREE.Object3D[] {
  const wrappers: THREE.Object3D[] = [];
  let current = bone.parent;
  const resolvedParentSignature = resolvedParentBone
    ? buildBoneIdentitySignature(resolvedParentBone)
    : null;

  while (current) {
    if (resolvedParentBone && current === resolvedParentBone) {
      break;
    }
    if (
      current instanceof THREE.Bone &&
      resolvedParentSignature &&
      buildBoneIdentitySignature(current) === resolvedParentSignature
    ) {
      break;
    }
    if (shouldIncludeTransformObject(current)) {
      wrappers.push(current);
    }
    current = current.parent;
  }

  wrappers.reverse();
  return wrappers;
}

function buildOutlinerHierarchy(
  bones: CartographerBone[],
  threeBones: THREE.Bone[],
  resolvedParentIndices: Array<number | null>,
  transformLocalOverrides?: Map<string, THREE.Matrix4>,
): OutlinerHierarchy {
  const nodesById: OutlinerHierarchy['nodesById'] = {};
  const boneNodeIdByIndex: string[] = [];

  for (const bone of bones) {
    const id = getBoneOutlinerNodeId(bone.index);
    boneNodeIdByIndex[bone.index] = id;
    nodesById[id] = {
      kind: 'bone',
      id,
      name: bone.name,
      boneIndex: bone.index,
      parentId: null,
      childrenIds: [],
    };
  }

  const attachNodeToParent = (childId: string, parentId: string | null) => {
    const childNode = nodesById[childId];
    if (!childNode) return;

    if (childNode.parentId === null) {
      childNode.parentId = parentId;
    } else if (parentId !== null && childNode.parentId !== parentId) {
      return;
    }

    if (!parentId) return;
    const parentNode = nodesById[parentId];
    if (!parentNode) return;
    if (!parentNode.childrenIds.includes(childId)) {
      parentNode.childrenIds.push(childId);
    }
  };

  const ensureTransformNode = (object: THREE.Object3D): OutlinerTransformNode => {
    const transformId = getTransformOutlinerNodeId(object);
    const existing = nodesById[transformId];
    if (existing && existing.kind === 'transform') return existing;

    const created = createTransformOutlinerNode(
      object,
      transformLocalOverrides?.get(object.uuid),
    );
    nodesById[transformId] = created;
    return created;
  };

  for (let boneIndex = 0; boneIndex < bones.length; boneIndex += 1) {
    const boneNodeId = boneNodeIdByIndex[boneIndex];
    const skeletonParentIndex = bones[boneIndex].parent;
    let parentNodeId =
      skeletonParentIndex !== null
        ? (boneNodeIdByIndex[skeletonParentIndex] ?? null)
        : null;

    if (boneIndex < threeBones.length) {
      const sourceBone = threeBones[boneIndex];
      const resolvedParentIndex = resolvedParentIndices[boneIndex] ?? null;
      const resolvedParentBone =
        resolvedParentIndex !== null ? threeBones[resolvedParentIndex] : null;
      const wrappers = collectHierarchyTransforms(sourceBone, resolvedParentBone);

      for (const wrapper of wrappers) {
        const transformNode = ensureTransformNode(wrapper);
        attachNodeToParent(transformNode.id, parentNodeId);
        parentNodeId = transformNode.id;
      }
    }

    attachNodeToParent(boneNodeId, parentNodeId);
  }

  const rootIds: string[] = [];
  for (const node of Object.values(nodesById)) {
    if (node.parentId === null) {
      rootIds.push(node.id);
    }
  }

  if (rootIds.length === 0 && boneNodeIdByIndex.length > 0) {
    rootIds.push(boneNodeIdByIndex[0]);
  }

  return { rootIds, nodesById, boneNodeIdByIndex };
}

function buildSyntheticRootName(existingNames: Set<string>, fileName?: string): string {
  const base = fileName ? `_${fileName.replace(/\.\w+$/, '')}` : '_Skeleton';
  if (!existingNames.has(base)) return base;
  let suffix = 1;
  while (existingNames.has(`${base}_${suffix}`)) suffix++;
  return `${base}_${suffix}`;
}

export type ExtractionResult = {
  skeleton: CartographerSkeleton;
  weightsInfo: Map<number, boolean>;
  /** Quaternion to apply to the scene for display after FBX up-axis correction. */
  orientationCorrection: THREE.Quaternion | null;
  outlinerHierarchy: OutlinerHierarchy;
  /**
   * When armature vs mesh bounds diverge (common with FBXLoader), we scale
   * `skeleton.rootParentWorld` by this factor. Apply the same factor to the
   * loaded scene root in Cartographer so SkinnedMesh + bones stay in sync.
   */
  meshArmatureUniformScale?: number;
};

/**
 * Extract a Cartographer Skeleton from a loaded three.js GLTF scene.
 *
 * Throws if no SkinnedMesh / skeleton is found.
 */
/**
 * Determine which skeleton bone indices are actually referenced by
 * vertex skinning data (JOINTS_0 attribute).  Mirrors the Python
 * importer's _find_deforming_joints — bones not referenced by any
 * vertex are marked deforming=false.
 */
function findDeformingBoneIndices(
  scene: THREE.Object3D,
  boneToIndex: Map<THREE.Object3D, number>,
  signatureToIndex: Map<string, number>,
  uniqueNameToIndex: Map<string, number>,
): Set<number> {
  const deforming = new Set<number>();

  scene.traverse((obj) => {
    if (!(obj instanceof THREE.SkinnedMesh) || !obj.skeleton) return;
    const mesh = obj as THREE.SkinnedMesh;
    const geom = mesh.geometry;
    if (!geom) return;

    const skinIndex =
      geom.getAttribute('skinIndex') as THREE.BufferAttribute | null;
    const skinWeight =
      geom.getAttribute('skinWeight') as THREE.BufferAttribute | null;
    if (!skinIndex || !skinWeight) return;

    const meshBones = mesh.skeleton.bones;
    const count = skinIndex.count;
    const itemSize = skinIndex.itemSize;
    for (let v = 0; v < count; v++) {
      for (let c = 0; c < itemSize; c++) {
        const weight = (skinWeight.array as Float32Array)[v * itemSize + c];
        if (weight > 0) {
          const localIdx = (skinIndex.array as Uint16Array)[v * itemSize + c];
          if (localIdx < meshBones.length) {
            let globalIdx = boneToIndex.get(meshBones[localIdx]);
            if (globalIdx === undefined) {
              const meshBone = meshBones[localIdx];
              if (meshBone) {
                globalIdx = signatureToIndex.get(
                  buildBoneIdentitySignature(meshBone),
                );
                if (globalIdx === undefined) {
                  globalIdx = uniqueNameToIndex.get(meshBone.name);
                }
              }
            }
            if (globalIdx !== undefined) deforming.add(globalIdx);
          }
        }
      }
    }
  });

  return deforming;
}

export function extractSkeleton(
  scene: THREE.Group,
  fileName?: string,
  options: ExtractSkeletonOptions = {},
): ExtractionResult {
  const { bones: allSceneBones } = collectAllBones(scene);
  if (allSceneBones.length === 0) {
    throw new Error(
      'No bones found in the scene. ' +
      'The model may not contain a rigged skeleton.',
    );
  }

  const threeBones = allSceneBones;

  // Force world matrix update so matrixWorld is current
  scene.updateMatrixWorld(true);

  // Map skeleton.bones by object identity for fast lookup
  const boneToIndex = new Map<THREE.Object3D, number>();
  const signatureToIdx = new Map<string, number>();
  const nameCounts = new Map<string, number>();
  const uniqueNameToIdx = new Map<string, number>();
  for (let i = 0; i < threeBones.length; i++) {
    const name = threeBones[i].name;
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }
  for (let i = 0; i < threeBones.length; i++) {
    boneToIndex.set(threeBones[i], i);
    signatureToIdx.set(buildBoneIdentitySignature(threeBones[i]), i);
    const name = threeBones[i].name;
    if ((nameCounts.get(name) ?? 0) === 1) {
      uniqueNameToIdx.set(name, i);
    }
  }

  const deformingIndices = findDeformingBoneIndices(
    scene,
    boneToIndex,
    signatureToIdx,
    uniqueNameToIdx,
  );

  // Diagnostic: check raw three.js bone TRS to verify FBXLoader output
  {
    const japaneseNames = ['全ての親', 'センター', 'グルーブ', '上半身', '下半身', '足_L', '足_R', 'ひざ_L', 'ひざ_R', '腕_L', '腕_R'];
    const interesting = threeBones.filter(b => japaneseNames.includes(b.name));
    if (interesting.length > 0) {
      console.log(`[Cartographer] Raw three.js bone TRS (${interesting.length} structural bones):`);
      for (const b of interesting) {
        const p = b.position;
        const mw = b.matrixWorld.elements;
        console.log(
          `[Cartographer]   ${b.name}: pos=(${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)}) ` +
          `matrixWorld.pos=(${mw[12].toFixed(3)}, ${mw[13].toFixed(3)}, ${mw[14].toFixed(3)}) ` +
          `parent=${b.parent?.name ?? 'null'}`
        );
      }
    }
  }

  // Build Cartographer bones
  const bones: CartographerBone[] = [];
  const resolvedParentIndices: Array<number | null> = [];
  for (let i = 0; i < threeBones.length; i++) {
    const tb = threeBones[i];

    // Parent: walk up the scene-graph parent chain to find the nearest
    // ancestor that is ALSO in skeleton.bones.  This handles both:
    // - GLTF: direct parent is usually in skeleton.bones
    // - FBX: FBXLoader creates wrapper Bone nodes (same name as the
    //   bone) between each skeleton bone, so we skip through them.
    let parentIdx: number | null = null;
    {
      let cur = tb.parent;
      while (cur) {
        const pi = boneToIndex.get(cur);
        if (pi !== undefined && pi !== i) {
          parentIdx = pi;
          break;
        }
        // Signature fallback: after dedup, this bone's scene-graph parent
        // may be a different mesh's copy that wasn't kept.
        if (cur instanceof THREE.Bone) {
          const bySignature = signatureToIdx.get(buildBoneIdentitySignature(cur));
          if (bySignature !== undefined && bySignature !== i) {
            parentIdx = bySignature;
            break;
          }
          const byUniqueName = uniqueNameToIdx.get(cur.name);
          if (byUniqueName !== undefined && byUniqueName !== i) {
            parentIdx = byUniqueName;
            break;
          }
        }
        cur = cur.parent;
      }
    }
    resolvedParentIndices[i] = parentIdx;

    // Children will be derived from parent relationships in a second
    // pass (see below).  This guarantees a tree with no cycles.

    // For restLocal, accumulate transforms through any wrapper/intermediate
    // nodes between this bone and its resolved parent.  This ensures the
    // local transform captures the full offset even when FBXLoader inserts
    // intermediate wrapper nodes between skeleton bones.
    let restLocal = boneRestLocal(tb);
    if (parentIdx !== null) {
      const parentObj = threeBones[parentIdx];
      const parentSignature = buildBoneIdentitySignature(parentObj);
      let cur = tb.parent;
      while (cur && cur !== parentObj) {
        // Stop at the signature-matched parent when bones are from different meshes.
        if (
          cur instanceof THREE.Bone &&
          buildBoneIdentitySignature(cur) === parentSignature
        ) {
          break;
        }
        const wrapperLocal = boneRestLocal(cur);
        restLocal = new THREE.Matrix4().multiplyMatrices(wrapperLocal, restLocal);
        cur = cur.parent;
      }
    }

    // Fallback: if the accumulated TRS is identity but the bone's matrixWorld
    // shows a real offset from its parent, derive restLocal from matrixWorld.
    // This handles FBX bones whose TRS was never populated (the FBXLoader
    // used boneInverses for skinning and left TRS at zero).
    {
      const el = restLocal.elements;
      const isIdentity =
        Math.abs(el[0] - 1) < 1e-6 && Math.abs(el[5] - 1) < 1e-6 && Math.abs(el[10] - 1) < 1e-6 &&
        Math.abs(el[12]) < 1e-6 && Math.abs(el[13]) < 1e-6 && Math.abs(el[14]) < 1e-6 &&
        Math.abs(el[1]) < 1e-6 && Math.abs(el[2]) < 1e-6 && Math.abs(el[4]) < 1e-6 &&
        Math.abs(el[6]) < 1e-6 && Math.abs(el[8]) < 1e-6 && Math.abs(el[9]) < 1e-6;
      if (isIdentity) {
        const parentWorld = parentIdx !== null
          ? threeBones[parentIdx].matrixWorld
          : (scene.matrixWorld ?? new THREE.Matrix4());
        const candidate = new THREE.Matrix4()
          .copy(parentWorld)
          .invert()
          .multiply(tb.matrixWorld);
        const ce = candidate.elements;
        const candidateIsIdentity =
          Math.abs(ce[0] - 1) < 1e-6 && Math.abs(ce[5] - 1) < 1e-6 && Math.abs(ce[10] - 1) < 1e-6 &&
          Math.abs(ce[12]) < 1e-6 && Math.abs(ce[13]) < 1e-6 && Math.abs(ce[14]) < 1e-6;
        if (!candidateIsIdentity) {
          restLocal = candidate;
        }
      }
    }
    const isDeforming =
      deformingIndices.size > 0 ? deformingIndices.has(i) : true;

    const resolvedEulerOrder = resolveBoneEulerOrder(tb);
    const internalName = tb.name || `Bone_${i}`;
    const srcName = sourceNameFromThreeBone(tb) || internalName;

    bones.push(
      new CartographerBone({
        index: i,
        name: internalName,
        sourceName: srcName,
        parent: parentIdx,
        children: [],
        restLocal,
        deforming: isDeforming,
        eulerOrder: resolvedEulerOrder.order,
        eulerOrderSource: resolvedEulerOrder.source,
      }),
    );
  }

  // Derive children from parent relationships (cycle-free by construction)
  for (const bone of bones) {
    if (bone.parent !== null && bone.parent >= 0 && bone.parent < bones.length) {
      bones[bone.parent].children.push(bone.index);
    }
  }

  // Find skeleton root: the bone with no parent
  let rootIdx = bones.findIndex((b) => b.parent === null);
  if (rootIdx === -1) rootIdx = 0;

  // Capture the scene-level transform of the container above the root bone
  // (e.g. Armature). This ensures the skeleton is rendered in the same
  // coordinate space as the mesh.
  // Skip ONLY genuine identity wrapper Bones that the FBXLoader inserts
  // between skeleton bones. A parent Bone that carries a real transform must
  // be kept: e.g. an FBX `root`/axis-conversion bone holding a -90° PreRotation
  // that is excluded from the SkinnedMesh skeleton because it is non-deforming.
  // Skipping it would drop the up-axis correction and lay the skeleton down.
  let rootContainer: THREE.Object3D | null = threeBones[rootIdx]?.parent ?? null;
  while (
    rootContainer instanceof THREE.Bone &&
    isIdentityLocalTransform(rootContainer)
  ) {
    rootContainer = rootContainer.parent;
  }
  const rootParentWorld = rootContainer
    ? rootContainer.matrixWorld.clone()
    : new THREE.Matrix4();

  // Optionally add synthetic root if the real root has a non-identity rest_local.
  // Python's GLTF importer does this, but Python's FBX importer never adds one —
  // FBX roots keep their full local transform (including PreRotation / axis
  // conversion) and rootParentWorld provides the container offset.
  const isFBX = fileName ? /\.fbx$/i.test(fileName) : false;
  if (!isFBX && needsSyntheticRoot(bones[rootIdx].restLocal)) {
    const syntheticIdx = bones.length;
    const existingNames = new Set(bones.map((b) => b.name));
    bones.push(
      new CartographerBone({
        index: syntheticIdx,
        name: buildSyntheticRootName(existingNames, fileName),
        parent: null,
        children: [rootIdx],
        deforming: false,
        auxiliary: true,
        eulerOrder: EulerOrder.XYZ,
        eulerOrderSource: 'default',
      }),
    );
    bones[rootIdx].parent = syntheticIdx;
    rootIdx = syntheticIdx;
  }

  const skeleton = new CartographerSkeleton(bones, rootIdx, rootParentWorld);
  skeleton.computeWorldTransforms();
  skeleton.computePosedWorldTransforms();
  skeleton.computeDepthsAndSubtrees();
  skeleton.computeLengthsAndDirections();

  // Diagnostic: dump bone positions and lengths to help debug zero-length issues
  {
    const zeroLenBones: string[] = [];
    const sampleBones: string[] = [];
    const japaneseStructural = ['全ての親', 'センター', 'グルーブ', '上半身', '下半身', '足_L', '足_R', 'ひざ_L', 'ひざ_R', '腕_L', '腕_R'];
    for (const b of bones) {
      if (b.length < 1e-6) zeroLenBones.push(b.name);
      if (japaneseStructural.some(jp => b.name === jp) || sampleBones.length < 5) {
        const e = b.restWorld.elements;
        const lm = b.restLocal.elements;
        sampleBones.push(
          `  ${b.name}[${b.index}]: worldPos=(${e[12].toFixed(3)}, ${e[13].toFixed(3)}, ${e[14].toFixed(3)}) ` +
          `localTrans=(${lm[12].toFixed(3)}, ${lm[13].toFixed(3)}, ${lm[14].toFixed(3)}) ` +
          `len=${b.length.toFixed(4)} children=[${b.children.map(c => bones[c]?.name).join(',')}]`
        );
      }
    }
    console.log(`[Cartographer] Bone diagnostics (${bones.length} bones, ${zeroLenBones.length} zero-length):`);
    for (const line of sampleBones) console.log(`[Cartographer]${line}`);
    if (zeroLenBones.length > 0 && zeroLenBones.length <= 60) {
      console.log(`[Cartographer] Zero-length bones: ${zeroLenBones.join(', ')}`);
    }
  }

  // FBX files may be authored in any up-axis. The three.js FBXLoader parses
  // the scene but does not rotate it into Y-up for us, so we prefer explicit
  // axis metadata when available and only fall back to scene heuristics when
  // the file does not expose a usable up-axis declaration.
  let orientationCorrection: THREE.Quaternion | null = null;

  if (isFBX) {
    const fbxUpAxisMetadata = options.fbxUpAxisMetadata ?? null;
    if (fbxUpAxisMetadata) {
      const metadataCorrection = createFbxUpAxisCorrection(fbxUpAxisMetadata);

      if (metadataCorrection) {
        orientationCorrection = metadataCorrection;
        const correction = new THREE.Matrix4().makeRotationFromQuaternion(
          metadataCorrection,
        );
        skeleton.rootParentWorld.premultiply(correction);
        skeleton.computeWorldTransforms();
        skeleton.computePosedWorldTransforms();
        skeleton.computeLengthsAndDirections();
        console.log(
          `[Cartographer] Applied FBX up-axis metadata correction (${formatSignedAxisLabel(
            fbxUpAxisMetadata.upAxis,
            fbxUpAxisMetadata.upSign,
          )} -> +Y, ${fbxUpAxisMetadata.property})`,
        );
      } else {
        console.log(
          `[Cartographer] FBX up-axis metadata (${formatSignedAxisLabel(
            fbxUpAxisMetadata.upAxis,
            fbxUpAxisMetadata.upSign,
          )}, ${fbxUpAxisMetadata.property}) already indicates +Y; skipping orientation correction`,
        );
      }
    } else {
      let detectedZUp = false;

      // Method 1: Intrinsic leg-direction analysis.
      // Measure direction from each upper-leg bone to its child (lower leg).
      // This gives the "downward" direction regardless of where the hip socket
      // sits relative to the pelvis — works for bipeds and quadrupeds.
      //
      // Exclude twist/helper/roll bones — they add noise and can cancel out
      // the real leg direction. Deduplicate by name since multi-mesh FBX files
      // can have multiple bone instances with the same name.
      const isHelperBone = (name: string) =>
        /twist|helper|roll|aux|adjust/i.test(name);

      const isNonStructuralBone = (name: string) =>
        /guard|armor|socket|attach|prop|cloth|collid|phys/i.test(name);

      const isLegCandidate = (name: string) =>
        !isHelperBone(name) && !isNonStructuralBone(name);

      const deduplicateByName = (arr: CartographerBone[]): CartographerBone[] => {
        const seen = new Set<string>();
        return arr.filter((b) => {
          if (seen.has(b.name)) return false;
          seen.add(b.name);
          return true;
        });
      };

      let thighBones = deduplicateByName(
        bones.filter(
          (b) => /thigh|upper.?leg|up.?leg/i.test(b.name) && isLegCandidate(b.name),
        ),
      );
      if (thighBones.length < 2) {
        thighBones = deduplicateByName(
          bones.filter(
            (b) => /hip_[lr]|[lr]_hip/i.test(b.name) && isLegCandidate(b.name),
          ),
        );
      }
      // Fallback for Japanese MMD models: 足_L/足_R (upper leg) or 足.L/足.R
      if (thighBones.length < 2) {
        thighBones = deduplicateByName(
          bones.filter(
            (b) => /^足[._][LRlr]$/i.test(b.name) && isLegCandidate(b.name),
          ),
        );
      }

      const legDirs: THREE.Vector3[] = [];
      for (const tb of thighBones) {
        if (tb.children.length === 0) continue;
        const child = bones[tb.children[0]];
        if (!child) continue;
        const te = tb.restWorld.elements;
        const ce = child.restWorld.elements;
        legDirs.push(
          new THREE.Vector3(ce[12] - te[12], ce[13] - te[13], ce[14] - te[14]),
        );
      }

      let legZSign = 0;

      if (legDirs.length >= 2) {
        const avgDir = new THREE.Vector3();
        for (const d of legDirs) avgDir.add(d);
        avgDir.divideScalar(legDirs.length);
        const absX = Math.abs(avgDir.x);
        const absY = Math.abs(avgDir.y);
        const absZ = Math.abs(avgDir.z);
        console.log(
          `[Cartographer] Leg-direction check (FBX): bones=[${thighBones.map((b) => b.name).join(', ')}]` +
          ` intrinsic avgDir=[${avgDir.x.toFixed(2)}, ${avgDir.y.toFixed(2)}, ${avgDir.z.toFixed(2)}]`,
        );
        if (absZ > absY && absZ > absX) {
          detectedZUp = true;
          legZSign = Math.sign(avgDir.z);
          console.log('[Cartographer] Z-up detected via leg-direction analysis');
        }
      }

      // Method 2: Bounding-box fallback (catches non-humanoid FBX without leg bones)
      if (!detectedZUp && legDirs.length < 2) {
        const minB = new THREE.Vector3(Infinity, Infinity, Infinity);
        const maxB = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
        for (const b of bones) {
          const e = b.restWorld.elements;
          const p = new THREE.Vector3(e[12], e[13], e[14]);
          minB.min(p);
          maxB.max(p);
        }
        const ext = new THREE.Vector3().subVectors(maxB, minB);
        console.log(
          `[Cartographer] BBox fallback (FBX): extent=[${ext.x.toFixed(2)}, ${ext.y.toFixed(2)}, ${ext.z.toFixed(2)}]`,
        );
        // For FBX files, Z > Y (height > depth) is the meaningful signal.
        // X can exceed Z for T-posed characters (arm span > height).
        if (ext.z > ext.y) {
          detectedZUp = true;
          console.log('[Cartographer] Z-up detected via bounding-box fallback');
        }
      }

      if (detectedZUp) {
        // Heuristic fallback only runs when the file did not expose usable
        // axis metadata. Legs point "downward": if they go in +Z, up is -Z
        // → Rx(+90°). If legs go in -Z (or unknown via bbox fallback), up is
        // +Z → Rx(-90°).
        const correctionAngle = legZSign > 0 ? Math.PI / 2 : -Math.PI / 2;
        const correction = new THREE.Matrix4().makeRotationX(correctionAngle);
        skeleton.rootParentWorld.premultiply(correction);
        skeleton.computeWorldTransforms();
        skeleton.computePosedWorldTransforms();
        skeleton.computeLengthsAndDirections();
        // Only correct the mesh when the FBXLoader didn't already handle
        // orientation.  When legZSign > 0, rootParentWorld already included the
        // axis conversion so the mesh geometry is already upright.
        if (legZSign <= 0) {
          orientationCorrection = normalizeQuaternion(
            new THREE.Quaternion().setFromAxisAngle(
              new THREE.Vector3(1, 0, 0),
              correctionAngle,
            ),
          );
        }
        const angleDeg = ((correctionAngle * 180) / Math.PI).toFixed(0);
        console.log(
          `[Cartographer] Applied ${angleDeg}° X correction for Z-up orientation (mesh correction: ${legZSign <= 0})`,
        );
      } else {
        console.log('[Cartographer] FBX orientation check: no Z-up detected');
      }
    }
  } else {
    console.log(
      `[Cartographer] Skipping orientation correction (GLTF/GLB is Y-up by spec)`,
    );
  }

  // Scale normalization: some FBX files have the Armature at a much larger
  // scale than the mesh geometry (the FBXLoader normalizes mesh vertices
  // separately from the bone hierarchy).  Compare skeleton extent with mesh
  // extent and rescale rootParentWorld if they diverge significantly.
  let meshArmatureUniformScale: number | undefined;
  {
    const meshBox = new THREE.Box3().setFromObject(scene);
    if (!meshBox.isEmpty() && skeleton.bones.length > 2) {
      const skelBox = new THREE.Box3();
      for (const b of skeleton.bones) {
        const e = b.restWorld.elements;
        skelBox.expandByPoint(new THREE.Vector3(e[12], e[13], e[14]));
      }
      if (!skelBox.isEmpty()) {
        const meshDiag = meshBox.getSize(new THREE.Vector3()).length();
        const skelDiag = skelBox.getSize(new THREE.Vector3()).length();
        if (meshDiag > 1e-6 && skelDiag > 1e-6) {
          const ratio = skelDiag / meshDiag;
          if (ratio > 2 || ratio < 0.5) {
            const scaleFix = meshDiag / skelDiag;
            meshArmatureUniformScale = scaleFix;
            console.log(
              `[Cartographer] Scale mismatch: skeleton/mesh ratio=${ratio.toFixed(2)}, applying scale ${scaleFix.toFixed(6)}`,
            );
            skeleton.rootParentWorld.scale(
              new THREE.Vector3(scaleFix, scaleFix, scaleFix),
            );
            skeleton.computeWorldTransforms();
            skeleton.computePosedWorldTransforms();
            skeleton.computeLengthsAndDirections();
          }
        }
      }
    }
  }

  const weightsInfo = new Map<number, boolean>();
  for (const b of bones) weightsInfo.set(b.index, true);

  const transformLocalOverrides = new Map<string, THREE.Matrix4>();
  if (rootContainer) {
    const parentWorld =
      rootContainer.parent?.matrixWorld.clone() ?? new THREE.Matrix4();
    const correctedLocal = parentWorld
      .invert()
      .multiply(skeleton.rootParentWorld.clone());
    transformLocalOverrides.set(rootContainer.uuid, correctedLocal);
  }

  const outlinerHierarchy = buildOutlinerHierarchy(
    bones,
    threeBones,
    resolvedParentIndices,
    transformLocalOverrides,
  );

  return {
    skeleton,
    weightsInfo,
    orientationCorrection,
    outlinerHierarchy,
    meshArmatureUniformScale,
  };
}
